import Link from "next/link";
import { NextRouter, useRouter } from "next/router";
import { useState } from "react";

import { useTranslation } from "../../core/translation.core";
import usePageBtnNavRange from "../../hooks/usePageNavBtnRange.hook";
import ChevronLeftIcon from "../../icons/chevronLeft.icon";
import ChevronRightIcon from "../../icons/chevronRight.icon";
import { inputCss } from "../reuseables/native.comp";

interface PageNavProps {
  page: number;
  size: number;
  total: number;
  pathname: string;
  query?: NextRouter["query"];
  onPageSizeChange?: (newSize: number) => void;
}

export default function PageNav({ page, size, total, pathname, query, onPageSizeChange }: PageNavProps) {
  const { t, msg } = useTranslation();
  const { push } = useRouter();
  const [navigatePage, setNavigatePage] = useState<number>(page);
  const { mobile, desktop, pageCount } = usePageBtnNavRange({
    total,
    size,
    currentPage: page,
    mobileBtnsNum: 3,
    desktopBtnsNum: 10,
  });

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (navigatePage === page) return;

    push({
      pathname: pathname,
      query: { ...query, page: navigatePage },
    });
  };

  if (!size || !total) return null;

  const pageSizeOptions = [10, 20, 50, 100];

  return (
    <div className="mt-2 py-4 xl:grid grid-cols-[1fr_auto_1fr]">
      <div></div>
      <div className="flex gap-2 justify-center items-center ">
        <NavLink disabled={page === 1} page={page - 1}>
          <ChevronLeftIcon className="w-5 h-5" />
        </NavLink>

        {/* Desktop nav List */}
        <div className="hidden md:flex gap-2 ">
          {desktop.map((i) => (
            <NavLink key={i} isActive={page === i} page={i} pathname={pathname} query={query}>
              {i}
            </NavLink>
          ))}
        </div>

        {/* mobile nav List */}
        <div className="flex md:hidden ">
          {mobile.map((i) => (
            <NavLink key={i} isActive={page === i} page={i} pathname={pathname} query={query}>
              {i}
            </NavLink>
          ))}
        </div>
        <NavLink disabled={page >= pageCount} page={page + 1}>
          <ChevronRightIcon className="w-5 h-5" />
        </NavLink>
      </div>
      <form
        onSubmit={handleSubmit}
        className="mt-4 md:mt-0 text-base place-self-end flex flex-wrap justify-center md:justify-end items-center"
      >
        <p className="basis-full md:basis-auto text-center md:border-r md:pr-2 md:border-black md:dark:border-white ">
          {t(msg.product.totalProducts)}: {total}
        </p>
        {onPageSizeChange && (
          <label className="mx-2">
            {t(msg.product.itemsPerPage)}
            <select
              className={`${inputCss} !w-20`}
              value={size}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="mx-2">
          {t(msg.product.page)}
          <input
            className={`${inputCss} !w-16 `}
            min={1}
            max={pageCount}
            type="number"
            value={navigatePage}
            onChange={(e) => setNavigatePage(e.target.valueAsNumber)}
          />
        </label>
        / {pageCount}
        <input
          type="submit"
          value={t(msg.product.go)}
          className="h-9 bg-primary text-white px-3 ml-2 py-1 cursor-pointer hover:bg-primary-dark hover:border-primary-dark"
        />
      </form>
    </div>
  );
}

interface NavButtonProps {
  children: React.ReactNode;
  isActive?: boolean;
  disabled?: boolean;
  page: number;
  pathname?: string;
  query?: NextRouter["query"];
  onClick?: () => void;
}

const NavLink = ({ children, isActive, disabled = false, page, pathname, query }: NavButtonProps) => {
  if (disabled) return null;

  let className = "w-8 h-8 flex justify-center items-center rounded-full border border-gray-300 ";

  if (isActive) {
    className += "bg-gray-500 text-white ";
  }

  if (disabled) {
    className += "bg-gray-200 text-white ";
  }

  return (
    <Link
      className={className}
      href={{
        pathname: pathname,
        query: { ...query, page },
      }}
    >
      {children}
    </Link>
  );
};
